import React, { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";

// Free OpenStreetMap Nominatim geocoder, debounced.
export default function AddressAutocomplete({ value, onChange, onSelect }) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (!value || value.trim().length < 3) {
      setSuggestions([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    timer.current = setTimeout(async () => {
      try {
        const url = `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&countrycodes=us&limit=6&q=${encodeURIComponent(
          value
        )}`;
        const res = await fetch(url, {
          headers: { Accept: "application/json" },
        });
        const data = await res.json();
        setSuggestions(Array.isArray(data) ? data : []);
        setOpen(true);
      } catch {
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 350);
    return () => timer.current && clearTimeout(timer.current);
  }, [value]);

  return (
    <div className="relative" ref={wrapRef}>
      <input
        data-testid="quiz-step3-address-input"
        type="text"
        autoComplete="off"
        value={value}
        onFocus={() => suggestions.length && setOpen(true)}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Start typing your address..."
        className="w-full border-2 border-slate-200 rounded-lg p-4 text-lg font-medium focus:border-[#FF8800] focus:ring-1 focus:ring-[#FF8800] transition-colors outline-none"
        style={{ fontFamily: "Montserrat, sans-serif" }}
      />
      {loading && (
        <Loader2
          data-testid="address-loading"
          className="absolute right-4 top-1/2 -translate-y-1/2 h-5 w-5 animate-spin text-[#FF8800]"
        />
      )}
      {open && suggestions.length > 0 && (
        <ul
          data-testid="address-suggestions"
          className="absolute z-20 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-xl max-h-72 overflow-auto"
        >
          {suggestions.map((s, i) => (
            <li key={s.place_id || i}>
              <button
                type="button"
                data-testid={`address-suggestion-${i}`}
                onClick={() => {
                  onChange(s.display_name);
                  setOpen(false);
                  if (onSelect) onSelect(s);
                }}
                className="w-full text-left px-4 py-3 hover:bg-orange-50 transition-colors text-sm font-medium text-[#001B33] border-b border-slate-100 last:border-b-0"
                style={{ fontFamily: "Montserrat, sans-serif" }}
              >
                {s.display_name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
